import { DateTime } from 'luxon';
import i18n from '@/i18n';

export const ACTIVE_STATUSES: BulkJobStatus[] = ['pending', 'processing'];
export const VALUE_ACTIONS: BulkEditAction[] = ['tags', 'allergens', 'diets'];
export const STATUS_COLORS: Record<string, string> = {
	pending: 'grey',
	processing: 'primary',
	completed: 'statusGreen',
	failed: 'statusRed',
	skipped: 'statusYellow',
	cancelled: 'statusYellow'
};

/**
 * Absolute date-time formatted in the active locale (e.g. "Apr 21, 2026, 11:25 PM").
 *
 * @param {string | null} iso
 * @return {string}
 */
export function formatDateTime(iso: string | null): string {
	if (!iso) {
		return '—';
	}
	return DateTime.fromISO(iso).setLocale(i18n.locale).toLocaleString(DateTime.DATETIME_MED);
}

/**
 * Absolute time-only formatted in the active locale (e.g. "11:25:40 PM").
 *
 * @param {string | null} iso
 * @return {string}
 */
export function formatTime(iso: string | null): string {
	if (!iso) {
		return '—';
	}
	return DateTime.fromISO(iso).setLocale(i18n.locale).toLocaleString(DateTime.TIME_WITH_SECONDS);
}

/**
 * Relative time in the active locale (e.g. "2 hours ago"). Falls back to absolute date-time.
 *
 * @param {string | null} iso
 * @return {string}
 */
export function formatRelative(iso: string | null): string {
	if (!iso) {
		return '—';
	}
	const dt: DateTime = DateTime.fromISO(iso).setLocale(i18n.locale);
	return dt.toRelative({ locale: i18n.locale }) || dt.toLocaleString(DateTime.DATETIME_MED);
}

/**
 * True if the ISO timestamp is within the last `withinDays` days.
 *
 * @param {string | null} iso
 * @param {number} [withinDays]
 * @return {boolean}
 */
export function isRecent(iso: string | null, withinDays: number = 7): boolean {
	if (!iso) {
		return false;
	}
	return DateTime.now().diff(DateTime.fromISO(iso), 'days').days <= withinDays;
}

/**
 * Relative (e.g. "2 hours ago") if within the last 7 days, otherwise absolute date-time.
 *
 * @param {string | null} iso
 * @return {string}
 */
export function formatJobTimestamp(iso: string | null): string {
	if (!iso) {
		return '';
	}
	const dt: DateTime = DateTime.fromISO(iso).setLocale(i18n.locale);
	if (isRecent(iso)) {
		return dt.toRelative({ locale: i18n.locale }) || dt.toLocaleString(DateTime.DATETIME_MED);
	}
	return dt.toLocaleString(DateTime.DATETIME_MED);
}

/**
 * Percentage of a job's work that has reached a terminal state. Capped at 100. An item edit counts
 * items, so the bar agrees with the "{completed} of {total} items updated" line beside it; every
 * other job type does one unit of work per location and keeps counting locations.
 *
 * @param {BulkJob | null} job
 * @return {number}
 */
export function computePercent(job: BulkJob | null): number {
	if (!job) {
		return 0;
	}
	if (job.itemCounts && job.totalItems) {
		const { completed, failed, skipped } = job.itemCounts;
		return Math.min(100, Math.round(((completed + failed + skipped) / job.totalItems) * 100));
	}
	if (!job.totalLocations) {
		return 0;
	}
	const { completed, failed, skipped } = job.counts;
	return Math.min(100, Math.round(((completed + failed + skipped) / job.totalLocations) * 100));
}

/**
 * Whether a bulk-edit action applies a list of values (tags/allergens/diets) rather than being a
 * plain on/off toggle (featured/upsell). Decides whether the action modal shows a value picker.
 *
 * @param {BulkEditAction} action
 * @return {boolean}
 */
export function carriesValues(action: BulkEditAction): boolean {
	return VALUE_ACTIONS.includes(action);
}

/**
 * How many filters are narrowing the menu-item list. A filter set only ever holds entries that
 * narrow something — the ones left off are dropped before it leaves the filters modal — so every
 * key counts: one per scalar, and one per selected tag / allergen / diet.
 *
 * @param {BulkMenuFilters} filters
 * @return {number}
 */
export function countActiveFilters(filters: BulkMenuFilters): number {
	return Object.values(filters)
		.reduce((total: number, value: unknown) => total + (Array.isArray(value) ? value.length : 1), 0);
}

/**
 * Localized name of what a bulk item edit did — "Assign tags", "Unassign allergens". Built from the
 * job's own echoed inputs, so it survives a page reload and works for historical jobs this client
 * never started. Returns "" for jobs that aren't item edits (menu pull, replication).
 *
 * @param {BulkJobItemEdit | null | undefined} itemEdit
 * @return {string}
 */
export function bulkEditActionTitle(itemEdit: BulkJobItemEdit | null | undefined): string {
	if (!itemEdit) {
		return '';
	}
	const suffix: string = itemEdit.assign ? 'assign' : 'unassign';
	return i18n.t(`shared.bulk_job.titles.${itemEdit.action}_${suffix}`);
}

/**
 * Localized elapsed duration between two ISO timestamps. Uses now() for completedAt if the
 * job is still running. Falls back to "" when startedAt is null.
 *
 * @param {string | null} startedAt
 * @param {string | null} completedAt
 * @return {string}
 */
export function formatElapsed(startedAt: string | null, completedAt: string | null): string {
	if (!startedAt) {
		return '';
	}
	const end: DateTime = completedAt ? DateTime.fromISO(completedAt) : DateTime.now();
	const diffSeconds: number = Math.max(0, Math.round(end.diff(DateTime.fromISO(startedAt), 'seconds').seconds));

	if (diffSeconds === 0) {
		return i18n.tc('shared.durations.seconds_under_abbr');
	}

	if (diffSeconds < 60) {
		return i18n.tc('shared.durations.seconds_abbr', diffSeconds, { n: diffSeconds });
	}

	const totalMinutes: number = Math.floor(diffSeconds / 60);
	if (totalMinutes < 60) {
		return i18n.tc('shared.durations.minutes_abbr', totalMinutes, { n: totalMinutes });
	}

	const hours: number = Math.floor(totalMinutes / 60);
	const remainingMinutes: number = totalMinutes % 60;
	const hoursLabel: string = i18n.tc('shared.durations.hours_abbr', hours, { n: hours });
	if (!remainingMinutes) {
		return hoursLabel;
	}
	const minutesLabel: string = i18n.tc('shared.durations.minutes_abbr', remainingMinutes, { n: remainingMinutes });
	return `${hoursLabel} ${minutesLabel}`;
}
